// Pure mappings between the stats.rs wire shape (SurveyStats) and what the
// dashboard's cards render. Kept as plain functions with no React/query
// dependency so they're trivially unit-testable (stats-mapping.test.ts) and
// so every card composes them the same way instead of each re-deriving its
// own reading of the wire shape.

import type { StepRow, Survey, SurveyStats } from "@/api/types";
import { choiceConfigSchema, multiConfigSchema } from "@/features/surveys/survey-schema";

const DAY_FORMAT: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
const TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };

/** A trend bucket's label: `tick` for the chart's axis, `tooltip` for the
 *  hover. An hour bucket (windows of 72 hours or less) reads as its time on
 *  the axis — with its date too at midnight and on the `first` tick, so the
 *  axis says which day it's on — and as date and time in the tooltip. A day
 *  bucket reads as its date. A week bucket (windows over 180 days, which can
 *  span years) reads as month and year on the axis and as "Week of" its
 *  Monday — Postgres' ISO week start, a UTC date, so it's formatted in UTC
 *  to keep that Monday in every timezone. */
export function bucketLabel(
    bucket: string,
    unit: SurveyStats["bucket"],
    where: "tick" | "tooltip",
    first = false,
): string {
    const date = new Date(bucket);
    if (Number.isNaN(date.getTime())) return bucket;
    if (unit === "hour") {
        const day = date.toLocaleDateString(undefined, DAY_FORMAT);
        const time = date.toLocaleTimeString(undefined, TIME_FORMAT);
        if (where === "tooltip") return `${day}, ${time}`;
        return first || date.getHours() === 0 ? `${day} ${time}` : time;
    }
    if (unit === "day") return date.toLocaleDateString(undefined, DAY_FORMAT);
    if (where === "tick")
        return date.toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: "UTC" });
    const monday = date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    });
    return `Week of ${monday}`;
}

/** Hour steps that divide a day, so every thinned axis has a tick at midnight. */
const HOUR_STEPS = [1, 2, 3, 4, 6, 12, 24] as const;
const MAX_HOUR_TICKS = 8;

/** The hour buckets the axis labels: every hour on a short window, else the
 *  hours on the smallest whole step (in the viewer's time) that leaves at
 *  most eight — midnight is always one, so the day change is always named. */
export function hourTicks(buckets: string[]): string[] {
    if (buckets.length <= MAX_HOUR_TICKS) return buckets;
    for (const step of HOUR_STEPS) {
        const ticks = buckets.filter((bucket) => new Date(bucket).getHours() % step === 0);
        if (ticks.length <= MAX_HOUR_TICKS) return ticks;
    }
    return buckets.filter((bucket) => new Date(bucket).getHours() === 0);
}

/** Month steps that divide a year, so a thinned axis keeps January. */
const MONTH_STEPS = [1, 2, 3, 6, 12] as const;
const MAX_WEEK_TICKS = 12;

/** The week buckets the axis labels: the first week of each month (UTC, as
 *  the buckets are), the window's first week standing for its month. Ticks
 *  read as month and year, so one per month never shows the same label
 *  twice; a long window keeps every n-th month for at most twelve. */
export function weekTicks(buckets: string[]): string[] {
    const monthOf = (bucket: string) => {
        const date = new Date(bucket);
        return date.getUTCFullYear() * 12 + date.getUTCMonth();
    };
    const starts = buckets.filter((bucket, n) => n === 0 || monthOf(bucket) !== monthOf(buckets[n - 1] ?? bucket));
    for (const step of MONTH_STEPS) {
        const ticks = step === 1 ? starts : starts.filter((bucket) => monthOf(bucket) % step === 0);
        if (ticks.length <= MAX_WEEK_TICKS) return ticks;
    }
    return starts.filter((bucket) => monthOf(bucket) % 12 === 0);
}

/** The Trend card's caption: how much time one point on the chart covers. */
export function bucketCaption(unit: SurveyStats["bucket"]): string {
    return `Responses per ${unit}`;
}

export interface HeatCellView {
    dow: number;
    hour: number;
    count: number;
    /** 0..1, `count` normalized against the busiest cell in the window. 0
     *  for every cell when the whole window is empty (no cell to divide by). */
    intensity: number;
}

// Monday-first reading (the mockup's week-starts-Monday grid), Sunday last —
// even though `heatmap.rs`'s own `dow` follows JS's Sunday=0..Saturday=6
// convention on the wire. This is purely a display order; the wire `dow`
// values on each returned cell are untouched.
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;
const HOURS_PER_DAY = 24;

/** 7 rows (Mon..Sat, Sun last) × 24 columns, densified from the sparse
 *  `heatmap` list — a (dow, hour) pair absent from `cells` is a real zero,
 *  not a gap, so every one of the 168 cells is always present in the result. */
export function heatmapGrid(cells: SurveyStats["heatmap"]): HeatCellView[][] {
    const countByKey = new Map<string, number>();
    let max = 0;
    for (const cell of cells) {
        countByKey.set(`${cell.dow}:${cell.hour}`, cell.count);
        if (cell.count > max) max = cell.count;
    }
    return DOW_ORDER.map((dow) =>
        Array.from({ length: HOURS_PER_DAY }, (_, hour) => {
            const count = countByKey.get(`${dow}:${hour}`) ?? 0;
            return { dow, hour, count, intensity: max > 0 ? count / max : 0 };
        }),
    );
}

/** The faintest non-empty cell's accent mix, in percent. Below this the
 *  tint is indistinguishable from an empty cell on either surface. */
export const HEATMAP_MIX_FLOOR = 20;

/** Percentage of the accent to mix into a cell's background — the one ramp
 *  the heatmap's cells and its legend share. An empty cell stays at 0 (the
 *  card paints it on the neutral surface instead); the faintest non-empty
 *  cell starts at `HEATMAP_MIX_FLOOR` so one response is visibly distinct
 *  from none, then the ramp runs linearly (so lightness stays monotone) to
 *  100% at the busiest cell. */
export function heatmapMix(intensity: number): number {
    if (intensity <= 0) return 0;
    return Math.round(HEATMAP_MIX_FLOOR + (100 - HEATMAP_MIX_FLOOR) * Math.min(1, intensity));
}

export interface HeatPeak {
    dow: number;
    hour: number;
    count: number;
}

/** The busiest (dow, hour) in the window — the one cell an accessible
 *  summary can name. First in wire order wins a tie; `null` when nothing was
 *  submitted at all (no cells, or every count 0). */
export function heatmapPeak(cells: SurveyStats["heatmap"]): HeatPeak | null {
    let peak: HeatPeak | null = null;
    for (const cell of cells) {
        if (cell.count > 0 && (peak === null || cell.count > peak.count)) {
            peak = { dow: cell.dow, hour: cell.hour, count: cell.count };
        }
    }
    return peak;
}

/** `null` when the window has no responses at all — a 0/0 division reads as
 *  "nothing to report" rather than a misleading 0%. */
export function completionRate(stats: SurveyStats): number | null {
    return stats.count === 0 ? null : stats.completedCount / stats.count;
}

/** `null` whenever either side has no average to speak of (an empty window,
 *  or an empty previous window) — never coerced to 0, which would read as
 *  "no change" instead of "not comparable". */
export function deltaVsPrevious(stats: SurveyStats): number | null {
    if (stats.averageScore === null || stats.previous.averageScore === null) return null;
    return stats.averageScore - stats.previous.averageScore;
}

/** `null` when the previous window had no responses — "not comparable",
 *  shown as "No earlier data", never as a change from zero. */
export function countDeltaVsPrevious(stats: SurveyStats): number | null {
    return stats.previous.count === 0 ? null : stats.count - stats.previous.count;
}

export interface OptionMetaEntry {
    label: string;
    icon: string | null;
}

/** The survey's step for a summary's `stepId` — the config that labels it. */
export function stepById(survey: Survey, stepId: string): StepRow | undefined {
    return survey.steps.find((step) => step.id === stepId);
}

/** The scored step, if the survey has one. */
export function metricStep(survey: Survey): StepRow | undefined {
    return survey.steps.find((step) => step.isMetric);
}

/** The top of the scored step's scale — its highest option value — so the
 *  raw average can be read as "3.2 / 4". `null` without a scored step, or
 *  when its config carries no numeric values (recipe.rs rejects such a step,
 *  but the tile then names the scale in words rather than invent a top). */
export function metricScaleMax(survey: Survey): number | null {
    const parsed = choiceConfigSchema.safeParse(metricStep(survey)?.config);
    if (!parsed.success) return null;
    const values = parsed.data.options
        .map((option) => option.value)
        .filter((value): value is number => typeof value === "number");
    return values.length === 0 ? null : Math.max(...values);
}

/** A choice step's options keyed by `option.key`, in the step's own
 *  configured order (a `Map` iterates in insertion order) — what lets a card
 *  sort its bars by the configured order without re-deriving the config. An
 *  option with no label falls back to its key; an absent step, a step of
 *  another kind, or a config that fails `choiceConfigSchema` yields an empty
 *  map, which callers treat like any unknown key: fall back to the raw key. */
export function optionMeta(step: StepRow | undefined): Map<string, OptionMetaEntry> {
    const map = new Map<string, OptionMetaEntry>();
    if (step?.kind !== "choice") return map;
    const parsed = choiceConfigSchema.safeParse(step.config);
    if (!parsed.success) return map;
    for (const option of parsed.data.options) {
        const label = option.label && option.label.trim() !== "" ? option.label : option.key;
        map.set(option.key, { label, icon: option.icon ?? null });
    }
    return map;
}

/** A multi-select step's key → label, across its flat options or both split
 *  sets (a key in both sets keeps the first label). Same degrade-to-empty
 *  contract as `optionMeta`. */
export function multiLabels(step: StepRow | undefined): Map<string, string> {
    const map = new Map<string, string>();
    if (step?.kind !== "multi") return map;
    const parsed = multiConfigSchema.safeParse(step.config);
    if (!parsed.success) return map;
    const items =
        parsed.data.mode === "flat" ? parsed.data.options : [...parsed.data.negative, ...parsed.data.positive];
    for (const item of items) {
        if (!map.has(item.key)) map.set(item.key, item.label.trim() || item.key);
    }
    return map;
}
